import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';

// Shares the "cards worth grading" radar with the nav (badge on the Grading tab)
// and the Grading page. Dismissable: dismissing marks the current cards as seen
// so the badge clears and only returns when a genuinely NEW card worth grading
// shows up, it never nags about the same card until you actually grade it.
const GradeRadarContext = createContext({ candidates: [], badgeCount: 0, hasData: false, loading: true, dismiss: () => {}, refresh: () => {} });

const dismissKey = (user) => `scn-grade-radar-dismissed-${user?.id || 'anon'}`;

function loadDismissed(user) {
  try { const raw = localStorage.getItem(dismissKey(user)); const arr = raw ? JSON.parse(raw) : []; return new Set(Array.isArray(arr) ? arr : []); }
  catch { return new Set(); }
}

export function GradeRadarProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState({ candidates: [], count: 0, hasData: false });
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => loadDismissed(user));

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/grading/radar`);
      setData({ candidates: res.data?.candidates || [], count: Number(res.data?.count) || 0, hasData: !!res.data?.hasData });
    } catch (e) {
      setData({ candidates: [], count: 0, hasData: false });
    } finally {
      setLoading(false);
    }
  }, [user]);

  // The radar hits CardHedger, so fetch once on login (not on every focus).
  useEffect(() => {
    if (!user) { setData({ candidates: [], count: 0, hasData: false }); setLoading(false); return; }
    setDismissed(loadDismissed(user));
    refresh();
    const onChange = () => refresh();
    window.addEventListener('scn-grading-changed', onChange);
    return () => window.removeEventListener('scn-grading-changed', onChange);
  }, [user, refresh]);

  // Only the cards the user hasn't already waved off.
  const fresh = useMemo(() => (data.candidates || []).filter(c => !dismissed.has(c.id)), [data.candidates, dismissed]);

  const dismiss = useCallback(() => {
    setDismissed(prev => {
      const next = new Set(prev);
      (data.candidates || []).forEach(c => next.add(c.id));
      // Keep the set bounded so it can't grow forever.
      const arr = [...next].slice(-200);
      try { localStorage.setItem(dismissKey(user), JSON.stringify(arr)); } catch {}
      return new Set(arr);
    });
  }, [data.candidates, user]);

  return (
    <GradeRadarContext.Provider value={{ candidates: fresh, badgeCount: fresh.length, hasData: data.hasData, loading, dismiss, refresh }}>
      {children}
    </GradeRadarContext.Provider>
  );
}

export function useGradeRadar() {
  return useContext(GradeRadarContext);
}
